import { useEffect } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Button } from '@zep-us/zus';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { PlanGatedQuizType } from '@/shared/plan/consts';
import { ZDialog } from '@/ui';
import type { RestrictedProQuiz } from '../utils/resolveRestrictedQuizContent';
const {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPanel,
  DialogFooter
} = ZDialog;
const QUIZ_TYPE_LABEL_KEY = {
  match: 'common:quizCard.typeBadge.match',
  order: 'common:quizCard.typeBadge.order'
} as const satisfies Record<PlanGatedQuizType, string>;
const DisabledEditAlertModalComponent = (props: {
  type: 'unknown' | 'plan';
  restrictedMap?: boolean;
  restrictedQuizzes?: readonly RestrictedProQuiz[];
  /** 제한 문항이 있으면 목록의 첫 문항을 넘긴다 — 호출부가 그 문항으로 이동시킨다. */
  onEdit?: (firstRestrictedQuiz?: RestrictedProQuiz) => void;
}) => {
  const {
    visible,
    remove
  } = useModal();
  const router = useRouter();
  const {
    t
  } = useTranslation(['build', 'common']);
  const restrictedQuizzes = props.restrictedQuizzes ?? [];
  const showMapRecovery = props.type === 'plan' && !!props.restrictedMap;
  const hasRecoveryTarget = showMapRecovery || restrictedQuizzes.length > 0;
  const showRestrictedQuizGuide = props.type === 'plan' && !showMapRecovery && restrictedQuizzes.length > 0;
  const showUnknownRestrictedQuizGuide = props.type === 'plan' && !hasRecoveryTarget;
  let dialogTitle = t('build:build.lockRecovery.editorUnavailableTooltip');
  if (props.type === 'plan') {
    dialogTitle = t('build:build.guide.disabledQuizEdit');
  }
  if (showRestrictedQuizGuide || showUnknownRestrictedQuizGuide) {
    dialogTitle = t('build:build.lockRecovery.restrictedQuizTitle');
  }
  const handleEdit = () => {
    remove();
    setTimeout(() => props.onEdit?.(restrictedQuizzes[0]), 0);
  };
  useEffect(() => {
    router.events.on('routeChangeStart', () => {
      remove();
    });
    return () => {
      router.events.off('routeChangeStart', () => {
        remove();
      });
    };
  }, [remove, router.events]);
  return <Dialog maxWidth={'320px'} open={visible} onClose={() => {
    remove();
  }} data-sentry-element="Dialog" data-sentry-component="DisabledEditAlertModalComponent" data-sentry-source-file="DisabledEditAlertModal.tsx">
      <DialogPanel data-sentry-element="DialogPanel" data-sentry-source-file="DisabledEditAlertModal.tsx">
        <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="DisabledEditAlertModal.tsx">
          <DialogTitle className={''} data-sentry-element="DialogTitle" data-sentry-source-file="DisabledEditAlertModal.tsx">{dialogTitle}</DialogTitle>
        </DialogHeader>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="DisabledEditAlertModal.tsx">
          {props.type === 'plan' ? <div className={'text-body-md font-medium'}>
              {(showRestrictedQuizGuide || showUnknownRestrictedQuizGuide) && <p className={'mb-sm'}>
                  {t(showRestrictedQuizGuide ? 'build:build.lockRecovery.restrictedQuizDescription' : 'build:build.lockRecovery.restrictedQuizUnknownDescription')}
                </p>}
              <ul className={'max-h-[240px] list-disc overflow-y-auto pl-md'}>
                {showMapRecovery && <li>
                    {t('build:build.lockRecovery.availableFreeMapAction')}
                  </li>}
                {restrictedQuizzes.map(quiz => <li key={quiz.index} data-testid="restricted-quiz-item">
                    {t('build:build.quiz.questionNumber', {
                number: quiz.index + 1
              })}{' '}
                    - {t(QUIZ_TYPE_LABEL_KEY[quiz.type])}
                  </li>)}
              </ul>
            </div> : <div className={'whitespace-pre-line text-body-md font-medium'}>
              {t('build.autoSave.sorryMessage')}
            </div>}
        </DialogContent>
        <DialogFooter className={'w-full'} data-sentry-element="DialogFooter" data-sentry-source-file="DisabledEditAlertModal.tsx">
          <div className={'flex w-full flex-col gap-xs'}>
            {props.type === 'plan' && <Button onClick={() => {
            void router.replace('/pricing');
          }}>
                {t('build:build.guide.proPlanUpgrade')}
              </Button>}
            <Button intent={'alternative'} onClick={props.type === 'plan' && hasRecoveryTarget ? handleEdit : () => remove()} data-sentry-element="Button" data-sentry-source-file="DisabledEditAlertModal.tsx">
              {props.type === 'plan' && hasRecoveryTarget ? t('common:quizListItem.moreBtn.edit') : t('common:global.close.button')}
            </Button>
          </div>
        </DialogFooter>
      </DialogPanel>
    </Dialog>;
};
export const DisabledEditAlertModal = NiceModal.create(DisabledEditAlertModalComponent);